// A script's characters and their casting (cam tag, per-script talent, registry default), on src/ui. HD-7: the migrated
// copy of ScriptCharactersPanel (components/VideoCard.jsx, retired at HD-8), used by the Longform series panel on the
// Scripts editor and by the Production page's Script stage.
// Same calls, same rules as the original:
//   GET  /api/scripts/:id/characters                 (format Skit / Mockumentary / Longform, Approved only)
//   GET  /api/team-members                           (talent picker; Producer / Power User / Admin)
//   GET  /api/client-characters?clientId=            (registry casting notes)
//   PATCH /api/scripts/:id/characters/:characterId   { camAssignment } | { talentId }
//   DELETE /api/scripts/:id/characters/:characterId  (one row) | /characters/stale (every stale row)
//   POST /api/scripts/:id/characters/refresh         (re-scan the body)
// DEF-108: the Uncast chip is a button that opens the talent picker; nothing in a row deletes except its remove button.
// D-111: a row with no per-script override shows the client default (registry), muted, not Uncast.
import { useState, useEffect, useCallback } from 'react';
import { Check, ChevronDown, RefreshCw, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Select from '../ui/Select';
import Tag from '../ui/Tag';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { clean } from '../shell/copy';

const TAL_ROLES = ['Producer', 'Power User', 'Power_User', 'Admin'];
const CAMS = [1, 2, 3, 4].map(n => ({ value: String(n), label: `Cam ${n}` }));

export default function ScriptCast({ scriptId, clientId, user, refreshKey }) {
  const canEdit = (user?.roles || []).some(r => TAL_ROLES.includes(r));
  const confirm = useConfirm();
  const toast = useToast();

  const [data,        setData]        = useState(null); // { characters, staleCharacterIds, talStatus }
  const [loading,     setLoading]     = useState(true);
  const [savingId,    setSavingId]    = useState(null);
  const [deletingId,  setDeletingId]  = useState(null);
  const [refreshing,  setRefreshing]  = useState(false);
  const [collapsed,   setCollapsed]   = useState(false);
  const [errMsg,      setErrMsg]      = useState('');
  const [talents,     setTalents]     = useState([]);
  const [registry,    setRegistry]    = useState([]);
  const [pickingId,   setPickingId]   = useState(null); // DEF-108: row whose talent picker is open
  useEffect(() => {
    if (!canEdit) return;
    api.get('/api/team-members')
      .then(r => setTalents((r.data || []).filter(m =>
        m.isActive !== false && m.roles?.some(x => x.role === 'Talent'))))
      .catch(() => {});
  }, [canEdit]);
  useEffect(() => {
    if (!clientId) return;
    api.get(`/api/client-characters?clientId=${clientId}`)
      .then(r => setRegistry(r.data.characters || []))
      .catch(() => {});
  }, [clientId]);
  const registryNote = (name) => {
    const key = String(name || '').replace(/^\s+|\s+$/g, '').toLowerCase();
    const row = registry.find(r => !r.isArchived &&
      String(r.name).replace(/^\s+|\s+$/g, '').toLowerCase() === key);
    return row?.castingNote || null;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/scripts/${scriptId}/characters`);
      setData(r.data);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [scriptId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  async function patchCharacter(characterId, patch) {
    setSavingId(characterId);
    setErrMsg('');
    try {
      const r = await api.patch(`/api/scripts/${scriptId}/characters/${characterId}`, patch);
      setData(prev => ({
        ...prev,
        // PATCH returns the bare row — keep the GET's registry-default fields (D-111).
        characters: prev.characters.map(c => c.id === characterId ? { ...c, ...r.data } : c),
      }));
      if (patch.talentId !== undefined) setPickingId(null);
    } catch (e) {
      setErrMsg(clean(e.response?.data?.error) || 'The change was not saved. Try again.');
    } finally { setSavingId(null); }
  }

  async function deleteCharacter(characterId, name) {
    const ok = await confirm({
      title: `Remove ${clean(name)}?`,
      body: 'The character row and its casting for this script are removed. This cannot be undone.',
      confirmLabel: 'Remove character',
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(characterId);
    setErrMsg('');
    try {
      await api.delete(`/api/scripts/${scriptId}/characters/${characterId}`);
      setData(prev => ({
        ...prev,
        characters: prev.characters.filter(c => c.id !== characterId),
        staleCharacterIds: (prev.staleCharacterIds || []).filter(id => id !== characterId),
      }));
    } catch (e) {
      setErrMsg(clean(e.response?.data?.error) || 'The character was not removed. Try again.');
    } finally { setDeletingId(null); }
  }

  async function refreshCharacters() {
    setRefreshing(true);
    setErrMsg('');
    try {
      await api.post(`/api/scripts/${scriptId}/characters/refresh`);
      await load();
    } catch (e) {
      setErrMsg(clean(e.response?.data?.error) || 'The refresh did not work. Try again.');
    } finally { setRefreshing(false); }
  }

  // Hide entirely until loaded so non-Skit/non-Approved scripts don't flash a loading line before the gate below.
  if (loading || !data) return null;

  // Skit + Mockumentary + Longform (the character-column formats, DEF-083), and only once the script is Approved.
  if (!['Skit', 'Mockumentary', 'Longform'].includes(data.format) || data.qualStatus !== 'Approved') return null;

  const { characters, staleCharacterIds = [], talStatus, scheduled, hasShootDay } = data;
  const staleSet   = new Set(staleCharacterIds);
  // D-095: one click clears every stale row (bad imports can leave dozens).
  const removeAllStale = async () => {
    const n = staleCharacterIds.length;
    const ok = await confirm({
      title: `Remove ${n} character${n === 1 ? '' : 's'} no longer in the script?`,
      body: 'Their rows and casting for this script are removed. This cannot be undone.',
      confirmLabel: 'Remove characters',
      destructive: true,
    });
    if (!ok) return;
    try { await api.delete(`/api/scripts/${scriptId}/characters/stale`); setData(prev => ({ ...prev, characters: prev.characters.filter(c => !staleSet.has(c.id)), staleCharacterIds: [] })); }
    catch (e) { toast(clean(e.response?.data?.error) || 'The stale characters were not removed. Try again.', { tone: 'danger' }); }
  };
  const liveChars  = characters.filter(c => !staleSet.has(c.id));
  const untagged   = liveChars.filter(c => !c.talReady);

  // Membership is derived (D-050): segments existing means Assigned; only a segment with a shoot day is Scheduled.
  const status = scheduled
    ? (hasShootDay ? <Badge tone="info">Scheduled</Badge> : <Badge tone="warn">Assigned</Badge>)
    : (talStatus === 'not_ready' ? <Tag>Hidden from the Script list</Tag> : <Badge tone="ok">In the Script list</Badge>);

  return (
    <div className="ui-stack">
      <div className="ui-section__head ui-section__head--center">
        <h4 className="ui-heading">
          <button type="button" className="ui-disclosure" aria-expanded={!collapsed} onClick={() => setCollapsed(v => !v)}>
            <Icon icon={ChevronDown} />Characters <span className="ui-meta ui-num">{characters.length}</span>
          </button>
        </h4>
        {status}
        {canEdit && !collapsed && (
          <span className="ui-section__actions">
            <Button variant="quiet" icon={RefreshCw} loading={refreshing} onClick={refreshCharacters} title="Scan the script again for new characters">
              {refreshing ? 'Refreshing' : 'Refresh'}
            </Button>
          </span>
        )}
      </div>

      {!collapsed && (
        <>
          {staleCharacterIds.length > 0 && canEdit && (
            <div className="ui-cluster">
              <Button variant="danger" icon={Trash2} data-testid="remove-stale-characters" onClick={removeAllStale}
                title="Delete every character row whose name is no longer in the script (bad imports can leave dozens)">
                Remove {staleCharacterIds.length} stale character{staleCharacterIds.length === 1 ? '' : 's'}
              </Button>
            </div>
          )}
          {errMsg && <p className="ui-p ui-dense ui-text-danger" role="alert">{errMsg}</p>}
          {characters.length === 0 ? (
            <p className="ui-p ui-dense ui-text-2">No characters found yet. Refresh scans the script for them.</p>
          ) : (
            <div className="ui-list">
              {characters.map(c => {
                const stale = staleSet.has(c.id);
                const note = clean(registryNote(c.characterName));
                const def  = c.defaultTalent || null;
                const picking = pickingId === c.id;
                const picker = canEdit && (
                  <Select
                    autoFocus={picking}
                    aria-label={`Talent for ${c.characterName}`}
                    data-testid="talent-picker"
                    value={c.talentId || ''}
                    disabled={savingId === c.id}
                    onChange={e => patchCharacter(c.id, { talentId: e.target.value || null })}
                    onBlur={() => { if (!c.talentId) setPickingId(null); }}
                    onKeyDown={e => { if (e.key === 'Escape') setPickingId(null); }}
                    title={def ? `Override for this script only. Leave it on the client default to follow the registry (${def.name}).` : 'Cast for this script only; the client default is unchanged'}
                    placeholder={def ? `Client default: ${def.name}` : 'Talent'}
                    options={[
                      ...talents.map(t => ({ value: t.id, label: t.name })),
                      ...(c.talentId && !talents.some(t => t.id === c.talentId) ? [{ value: c.talentId, label: c.talent?.name || 'Unknown member' }] : []),
                    ]}
                  />
                );
                let talentCell;
                if (c.talentId) {
                  // Cast for this script (override): the picker shows the name, editable in place.
                  talentCell = canEdit ? picker : <span className="ui-truncate">{c.talent?.name}</span>;
                } else if (picking && canEdit) {
                  // Picker open (chip clicked): the select replaces the chip until a choice / Escape / blur.
                  talentCell = picker;
                } else {
                  // No override: the client default (muted, D-111) or Uncast. Both open the picker.
                  talentCell = (
                    <>
                      {def ? (
                        <button type="button" className="ui-link ui-text-2" data-testid="default-talent-chip" disabled={!canEdit}
                          onClick={e => { e.stopPropagation(); if (canEdit) setPickingId(c.id); }}
                          title={canEdit ? `${def.name}: the client default from the character registry (not yet booked). Click to cast someone else for this script only.` : `${def.name}: the client default from the character registry`}>
                          {def.name}<span className="ui-meta">default</span>
                        </button>
                      ) : (
                        <button type="button" className="ui-tag" data-testid="uncast-chip" disabled={!canEdit}
                          onClick={e => { e.stopPropagation(); if (canEdit) setPickingId(c.id); }}
                          title={note
                            ? `Uncast: the writers' note ("${note}") is not a casting.${canEdit ? ' Click to pick talent for this script.' : ''}`
                            : (canEdit ? 'Uncast: click to pick talent for this script (or set a client default in the character registry)' : 'Uncast: a Producer, Power User or Admin casts this character')}>
                          Uncast{canEdit && <Icon icon={ChevronDown} />}
                        </button>
                      )}
                      {note && <span className="ui-meta ui-truncate"><em>{note}</em></span>}
                    </>
                  );
                }
                return (
                  <div key={c.id} className={`ui-castrow${canEdit ? ' ui-castrow--edit' : ''}`} data-stale={stale ? 'true' : undefined} aria-busy={deletingId === c.id || undefined}>
                    <span className="ui-stack ui-stack--tight">
                      <span className="ui-truncate">{c.characterName}</span>
                      {stale && <Badge tone="danger">No longer in the script</Badge>}
                    </span>
                    <Select
                      aria-label={`Camera for ${c.characterName}`}
                      value={c.camAssignment ? String(c.camAssignment) : ''}
                      disabled={!canEdit || savingId === c.id}
                      onChange={e => patchCharacter(c.id, { camAssignment: e.target.value ? Number(e.target.value) : null })}
                      placeholder="Cam"
                      options={CAMS}
                    />
                    <span data-testid="talent-cell" className="ui-cluster ui-cluster--nowrap">{talentCell}</span>
                    <span title={c.talReady ? 'Tagged' : 'Not tagged'}>{c.talReady ? <Icon icon={Check} label="Tagged" /> : null}</span>
                    {canEdit && (
                      <Button variant="quiet" iconOnly icon={X} label={`Remove ${c.characterName}`}
                        onClick={() => deleteCharacter(c.id, c.characterName)} disabled={deletingId === c.id || savingId === c.id} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Status line, no send button: Script list membership is derived (D-050), so the script is already there.
              Tags remain useful: cam tags drive the Schedule plan's grouping. */}
          {canEdit && characters.length > 0 && (
            <p className="ui-p ui-meta">
              {scheduled ? (
                hasShootDay
                  ? 'Scheduled: see the Shoot schedule (also listed on the Script list, Assigned tab).'
                  : "Assigned, no shoot day yet. Book talent on the Talent calendar, then schedule from the day's shootable scripts."
              ) : talStatus === 'not_ready' ? (
                'Hidden from the Ready tab of the Script list. Recover it there with Show hidden.'
              ) : (
                `In the Script list.${untagged.length > 0 ? ` ${untagged.length} character${untagged.length === 1 ? ' is' : 's are'} untagged; untagged characters plan as Cam 1. Add cam tags to refine the cam grouping.` : ''}`
              )}
            </p>
          )}
        </>
      )}
    </div>
  );
}
